import React from 'react';

import { isDefined } from '@nfctron/nexus-core';
import { useContextRequired } from '@nfctron/nexus-react';

import { useLocalStorage } from '@mantine/hooks';
import ReactCanvasConfetti from 'react-canvas-confetti';
import { type TCanvasConfettiInstance } from 'react-canvas-confetti/dist/types';
import Snowfall from 'react-snowfall';

import { type VisualEffectsContextProps } from '@root/features/layout/components/VisualEffects/VisualEffects.types';

export const VisualEffectsContext = React.createContext<VisualEffectsContextProps | undefined>(undefined);

export const VisualEffectsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const confettiInstanceRef = React.useRef<TCanvasConfettiInstance | null>(null);
	const [snowFallLocal, setSnowFallLocal] = useLocalStorage({
		key: 'fx_snowFall_2025',
		defaultValue: false,
	});
	const snowFallController = {
		value: snowFallLocal,
		toggle: () => setSnowFallLocal((current) => !current),
		enable: () => setSnowFallLocal(true),
		disable: () => setSnowFallLocal(false),
	};

	return (
		<VisualEffectsContext
			value={{
				confettiInstance: confettiInstanceRef.current,
				snowFallController,
			}}
		>
			{children}

			{/* confetti */}
			<ReactCanvasConfetti
				style={{
					zIndex: 99,
					position: 'fixed',
					top: 0,
					left: 0,
					width: '100%',
					height: '100%',
					pointerEvents: 'none',
				}}
				onInit={(instance) => {
					confettiInstanceRef.current = instance.confetti;
				}}
			/>

			{/* snow fall */}
			{snowFallController.value && (
				<Snowfall
					style={{
						zIndex: 99,
						position: 'fixed',
						top: 0,
						left: 0,
						width: '100%',
						height: '100%',
						pointerEvents: 'none',
					}}
				/>
			)}
		</VisualEffectsContext>
	);
};

export const useVisualEffects = () => {
	const ctx = useContextRequired(VisualEffectsContext);

	const confettiShootDefault = () => {
		if (!isDefined(ctx.confettiInstance)) return;
		ctx.confettiInstance({
			particleCount: 200,
			angle: 90,
			spread: 360,
			startVelocity: 35,
			decay: 0.9,
			gravity: 1.25,
			drift: 0,
			ticks: 1000,
			origin: {
				x: 0.5,
				y: 0.5,
			},
			colors: ['#25196A', '#7F46DB', '#ec4899'],
			shapes: ['circle', 'square'],
			scalar: 1,
			zIndex: 99,
			disableForReducedMotion: true,
		});
	};

	return {
		...ctx,
		confettiShootDefault,
	};
};
